import { useMemo } from 'react'
import { experimentSharingApi, shareApi, organizationsApi } from '@/lib/api'
import { ShareSettingsAdapter } from '@/components/sharing'

/**
 * Creates a ShareSettingsAdapter bound to a specific experiment.
 * Returns null when required context is unavailable.
 */
export function useExperimentShareAdapter(
  experimentId: string | null | undefined,
  organizationToken: string | null | undefined,
): ShareSettingsAdapter | null {
  return useMemo(() => {
    if (!experimentId || !organizationToken) return null

    const adapter: ShareSettingsAdapter = {
      async loadSharingData() {
        const data = await experimentSharingApi.getCollaborators(experimentId)
        return {
          sharingMode: (data.sharingMode || 'private') as 'private' | 'shared' | 'organization',
          collaborators: data.collaborators || [],
          guests: data.guests || [],
        }
      },
      async loadShareStatus() {
        const status = await shareApi.getStatus(experimentId)
        return {
          isPublic: status.isPublic || false,
          shareUrl: status.shareUrl || null,
        }
      },
      async loadOrgMembers() {
        return organizationsApi.getMembers(organizationToken)
      },
      async setSharingMode(mode) {
        await experimentSharingApi.setSharingMode(experimentId, mode)
      },
      async addCollaborator(userId) {
        await experimentSharingApi.addCollaborator(experimentId, userId)
      },
      async removeCollaborator(id) {
        await experimentSharingApi.removeCollaborator(experimentId, id)
      },
      async addGuest(email) {
        const result = await experimentSharingApi.addGuest(experimentId, email)
        return { invitationEmailSent: result.invitationEmailSent }
      },
      async removeGuest(id) {
        await experimentSharingApi.removeGuest(experimentId, id)
      },
      async enableSharing() {
        const result = await shareApi.enable(experimentId)
        return { shareUrl: result.shareUrl }
      },
      async disableSharing() {
        await shareApi.disable(experimentId)
      },
      async loadGuestChatSettings() {
        return experimentSharingApi.getGuestChat(experimentId)
      },
      async setGuestChat(enabled: boolean, cap: number) {
        await experimentSharingApi.setGuestChat(experimentId, enabled, cap)
      },
      async regenerateLink() {
        const result = await shareApi.regenerate(experimentId)
        return { shareUrl: result.shareUrl }
      },
      async ensureShareToken() {
        return shareApi.ensureShareToken(experimentId)
      },
    }

    return adapter
  }, [experimentId, organizationToken])
}
